import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LockOutlined,
  HistoryRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  RestartAltOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import type {
  WorkflowRequest,
  WorkflowRequestDraftApprovalStep,
  WorkflowTemplate,
} from '../model';
import type { WorkflowPort } from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { registerNavigationGuard } from '../../../shared/navigationGuard';
import { formatWorkflowFieldValue } from '../models/workflowActivityPresentation';
import {
  FeedbackSnackbar,
  useConfirmation,
  CompactEditButton,
  designTokens,
  FormDialog,
  FormErrorSummary,
  FormTextField,
  InteractiveCard,
  LoadingIndicator,
  PageScaffold,
  PillTabs,
  SaveStatus,
  ValueBadge,
  WorkspaceSurface,
  WorkspaceSurfaceHeaderActions,
  type WorkspaceSurfaceStatusDescriptor,
} from '../../../design-system';
import {
  WorkflowApprovalLineEditor,
  WorkflowRequestFields,
  WorkflowRequestTitleInput,
  type WorkflowCustomFieldRendererProps,
  type WorkflowFixedPersonPickerRendererProps,
  type WorkflowFixedRolePickerRendererProps,
} from '../WorkflowRequestComposerUI';
import {
  WorkflowFileFieldControl,
  WorkflowReferenceFieldControl,
} from '../WorkflowSpecialFieldControls';
import {
  WorkflowRichTextEditor,
  workflowRichTextHasContent,
} from '../WorkflowRichTextEditor';
import type {
  WorkflowApprovalRoleOption,
  WorkflowApprovalSettingsPort,
  WorkflowApprovalSettingsSnapshot,
} from '../approval-settings/public';
import { WorkflowActivityTimeline } from '../WorkflowActivityTimeline';
import {
  WorkflowPersonPicker,
  WorkflowOrganizationPicker,
  WorkflowRolePicker,
} from '../WorkflowReferencePickers';
import { resolveWorkflowTemplateIconKey } from '../workflowTemplateIcons';
import type {
  EmployeeDirectoryPort,
  EmployeeDirectorySnapshot,
} from '../../people/public';
import {
  fixtureApprovalLabels,
  fixtureApprovalTemplates,
  fixtureReferenceOptions,
  fixtureWorkflowApprovalRoles,
  fixtureWorkflowEmployeeDirectory,
} from '../fixtures/workflowPageFixtures';
import { submitFixtureWorkflowRequest } from '../fixtures/workflowRequestFixtureSupport';
import { useWorkflowDraftPersistence } from '../models/useWorkflowDraftPersistence';
import { WorkflowRequestTemplateTitleIcon } from './WorkflowRequestTemplateTitleIcon';
import { WorkflowRequestInformationSection } from './WorkflowRequestInformationSection';
import { workflowApprovalLabelBadgeTone } from '../workflowApprovalLabelPresentation';
import {
  approvalLineDialogTabs,
  approvalLinePreviews,
  cloneRequestApprovalSteps,
  requestApprovalStepsFromTemplate,
  reorderRequestSteps,
  type ApprovalLineDialogView,
  type WorkflowComposerPresentation,
  workflowFieldHasValue,
  workflowFieldValidationError,
  workflowRequestApprovalStepError,
  workflowPersonOptions,
} from '../models/workflowRequestPageModel';

export function WorkflowRequestComposer({ templates, initialTemplateId, editing, requesterWorkerId, presentation, onPresentationChange, onClose, onDraftPersisted, onComplete, onSubmissionResolved, port, approvalSettingsPort, directoryPort, fixtureMode, canCreate }: { templates: WorkflowTemplate[]; initialTemplateId?: string; editing?: WorkflowRequest; requesterWorkerId?: string; presentation: WorkflowComposerPresentation; onPresentationChange: (presentation: WorkflowComposerPresentation) => void; onClose: (savedDraftID?: string) => void; onDraftPersisted: (draftID: string) => void; onComplete: (action: 'draft' | 'submit', requestId: string, fixtureRequest?: WorkflowRequest) => void; onSubmissionResolved: (request: WorkflowRequest) => void; port: WorkflowPort; approvalSettingsPort: WorkflowApprovalSettingsPort; directoryPort: EmployeeDirectoryPort; fixtureMode: boolean; canCreate: boolean }) {
  const initialTemplate = templates.find((item) => item.id === (editing?.templateId ?? initialTemplateId));
  const templateId = editing?.templateId ?? initialTemplateId ?? '';
  const [title, setTitle] = useState(editing?.title ?? initialTemplate?.displayName ?? '');
  const [values, setValues] = useState<Record<string, unknown>>(editing?.values ?? {});
  const [approvalSteps, setApprovalSteps] = useState<WorkflowRequestDraftApprovalStep[]>(() => editing?.draftApprovalSteps !== undefined ? cloneRequestApprovalSteps(editing.draftApprovalSteps) : requestApprovalStepsFromTemplate(initialTemplate?.version.approvalSteps ?? []));
  const [approvalLineDraft, setApprovalLineDraft] = useState<WorkflowRequestDraftApprovalStep[]>(() => cloneRequestApprovalSteps(editing?.draftApprovalSteps !== undefined ? editing.draftApprovalSteps : requestApprovalStepsFromTemplate(initialTemplate?.version.approvalSteps ?? [])));
  const skipUnloadGuardRef = useRef(false);
  const [notice, setNotice] = useState('');
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<ReadonlySet<string>>(new Set());
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const composerID = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const composerRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const activityFeedRef = useRef<HTMLDivElement>(null);
  const [revealRevisionKey, setRevealRevisionKey] = useState(0);
  const smallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const fileBusyChanged = useCallback((key: string, busy: boolean) => {
    setPendingFiles((current) => {
      if (current.has(key) === busy) return current;
      const next = new Set(current); if (busy) next.add(key); else next.delete(key); return next;
    });
  }, []);
  const withoutNavigationGuard = <T,>(action: () => T): T => {
    skipUnloadGuardRef.current = true;
    try { return action(); } finally { skipUnloadGuardRef.current = false; }
  };
  const [activityOpen, setActivityOpen] = useState(editing?.status === 'NEEDS_CHANGES');
  const activityPanelID = useId();
  const [approvalLineOpen, setApprovalLineOpen] = useState(false);
  const [approvalPreviewOpen, setApprovalPreviewOpen] = useState(false);
  const approvalPreviewID = useId();
  const [approvalLineView, setApprovalLineView] = useState<ApprovalLineDialogView>('edit');
  const [approvalLineNotice, setApprovalLineNotice] = useState('');
  const [approvalTemplateId, setApprovalTemplateId] = useState('');
  const approvalSettings = useAsyncResource<WorkflowApprovalSettingsSnapshot>(
    () => fixtureMode
      ? Promise.resolve({ labels: fixtureApprovalLabels, templates: fixtureApprovalTemplates })
      : approvalSettingsPort.loadSnapshot(),
    [approvalSettingsPort, fixtureMode],
  );
  const directory = useAsyncResource<EmployeeDirectorySnapshot>(
    () => fixtureMode ? Promise.resolve(fixtureWorkflowEmployeeDirectory) : directoryPort.loadDirectory(),
    [directoryPort, fixtureMode],
  );
  const approvalRoleOptions = useAsyncResource<WorkflowApprovalRoleOption[]>(
    () => fixtureMode
      ? Promise.resolve(fixtureWorkflowApprovalRoles)
      : approvalSettingsPort.listRoleOptions('', 100),
    [approvalSettingsPort, fixtureMode],
  );
  const loadRoleOptions = useCallback((query: string) => fixtureMode
    ? Promise.resolve(fixtureWorkflowApprovalRoles.filter((role) => `${role.descriptor} ${role.roleCode}`.toLowerCase().includes(query.toLowerCase())))
    : approvalSettingsPort.listRoleOptions(query, 100), [approvalSettingsPort, fixtureMode]);
  const personOptions = useMemo(() => workflowPersonOptions(directory.data), [directory.data]);
  const template = templates.find((item) => item.id === templateId);
  const composerFields = editing?.fields ?? template?.version.fields ?? [];
  const formApprovalSteps = editing?.templateApprovalSteps ?? template?.version.approvalSteps;
  const propertyFields = composerFields.filter((field) => field.type !== 'LONG_TEXT');
  const documentFields = composerFields.filter((field) => field.type === 'LONG_TEXT');
  const submissionErrors = useMemo(() => Object.fromEntries(composerFields.map((field) => {
    const value = values[field.key];
    const missing = field.required && field.type !== 'DISPLAY_TEXT' && (field.type === 'LONG_TEXT' ? !workflowRichTextHasContent(typeof value === 'string' ? value : '') : !workflowFieldHasValue(field, value));
    const scanPending = field.type === 'FILE' && Array.isArray(value) && value.some((file) => file?.scanStatus && file.scanStatus !== 'CLEAN');
    return [field.key, missing ? '필수 항목을 입력해 주세요.' : workflowFieldValidationError(field, value) ?? (scanPending ? '첨부 파일의 보안 검사 상태를 확인해 주세요.' : undefined)];
  })), [composerFields, values]);
  const fieldErrors = useMemo(() => Object.fromEntries(composerFields.map((field) => [field.key, validationAttempt ? submissionErrors[field.key] : workflowFieldValidationError(field, values[field.key])])), [composerFields, values, validationAttempt, submissionErrors]);
  const approvalLabels = useMemo(() => {
    const labels = new Map<string, { id?: string; name: string; tone: 'GREEN' | 'BLUE' | 'PURPLE' | 'AMBER' | 'CYAN' | 'GRAY' }>();
    const addLabel = (label: { id?: string; name: string; tone: 'GREEN' | 'BLUE' | 'PURPLE' | 'AMBER' | 'CYAN' | 'GRAY' }) => {
      const key = label.id ? `id:${label.id}` : `name:${label.name}`;
      if (!labels.has(key)) labels.set(key, label);
    };
    addLabel({ name: '승인', tone: 'GREEN' });
    (approvalSettings.data?.labels ?? [])
      .filter((label) => Boolean(label.id) && label.status !== 'ARCHIVED')
      .forEach(({ id, name, tone }) => addLabel({ id, name, tone }));
    [...approvalSteps, ...approvalLineDraft].forEach((step) => {
      if (!step.approvalLabel?.name) return;
      addLabel({
        id: step.approvalLabelId ?? step.approvalLabel.id,
        name: step.approvalLabel.name,
        tone: step.approvalLabel.tone,
      });
    });
    return Array.from(labels.values());
  }, [approvalLineDraft, approvalSettings.data?.labels, approvalSteps]);
  const approvalPreviews = useMemo(
    () => approvalLinePreviews(approvalSteps, directory.data, requesterWorkerId, approvalRoleOptions.data ?? [], {
      directoryLoading: directory.loading,
      directoryError: directory.error?.message,
      rolesLoading: approvalRoleOptions.loading,
      rolesError: approvalRoleOptions.error?.message,
    }),
    [approvalRoleOptions.data, approvalRoleOptions.error?.message, approvalRoleOptions.loading, approvalSteps, directory.data, directory.error?.message, directory.loading, requesterWorkerId],
  );
  const approvalLineDraftPreviews = useMemo(
    () => approvalLinePreviews(approvalLineDraft, directory.data, requesterWorkerId, approvalRoleOptions.data ?? [], {
      directoryLoading: directory.loading,
      directoryError: directory.error?.message,
      rolesLoading: approvalRoleOptions.loading,
      rolesError: approvalRoleOptions.error?.message,
    }),
    [approvalLineDraft, approvalRoleOptions.data, approvalRoleOptions.error?.message, approvalRoleOptions.loading, directory.data, directory.error?.message, directory.loading, requesterWorkerId],
  );
  const renderFixedPersonPicker = ({ step, personID, disabled, onChange }: WorkflowFixedPersonPickerRendererProps) => <WorkflowPersonPicker
    label={`${step.label} 구성원 검색`}
    personId={personID}
    options={personOptions}
    loading={directory.loading}
    error={directory.error?.message}
    onRetry={directory.reload}
    disabled={disabled}
    onChange={onChange}
  />;
  const renderFixedRolePicker = ({ step, roleCode, disabled, onChange }: WorkflowFixedRolePickerRendererProps) => <WorkflowRolePicker
    loadOptions={loadRoleOptions}
    label={`${step.label} 승인 역할 검색`}
    roleCode={roleCode}
    options={approvalRoleOptions.data ?? []}
    loading={approvalRoleOptions.loading}
    error={approvalRoleOptions.error?.message}
    onRetry={approvalRoleOptions.reload}
    disabled={disabled}
    onChange={onChange}
  />;
  const canEditApprovalLine = editing?.status !== 'NEEDS_CHANGES' && approvalSteps.some((step) => step.requesterEditable);
  const approvalLineDraftIsValid = approvalLineDraft.length > 0
    && approvalLineDraft.every((step, index) => workflowRequestApprovalStepError(step, index, composerFields) === undefined);
  const approvalStepsAreValid = approvalSteps.length > 0
    && approvalSteps.every((step, index) => workflowRequestApprovalStepError(step, index, composerFields) === undefined);
  const readyToSave = Boolean((template || editing) && approvalStepsAreValid && pendingFiles.size === 0);
  const readyToSubmit = Boolean(readyToSave && title.trim() && Object.values(submissionErrors).every((error) => !error) && pendingFiles.size === 0);
  const {
    savedDraftID, isDirty, activityRequestID, activityRefreshKey,
    savingAction, draftSaveStatus, draftSaveError, draftSaveConflict, draftSavedAt,
    submitRetryPending, checkingSubmission, checkSubmission, error, approvalResolutionError, ensureDraftForFile, submit, retryDraftSave,
  } = useWorkflowDraftPersistence({
    input: { templateId, templateVersionId: editing?.templateVersionId ?? template?.version.id ?? '', title, values, approvalSteps },
    editing, port, fixtureMode, canCreate, readyToSave, readyToSubmit,
    onDraftPersisted: (id) => withoutNavigationGuard(() => onDraftPersisted(id)),
    onComplete: (action, result, snapshot) => {
      let fixtureRequest: WorkflowRequest | undefined;
      if (fixtureMode) {
        const now = new Date().toISOString();
        const requester = fixtureWorkflowEmployeeDirectory.members.find((member) => member.id === requesterWorkerId);
        fixtureRequest = {
          id: result.requestId, draftVersion: result.draftVersion,
          templateId: snapshot.templateId, templateVersionId: snapshot.templateVersionId,
          templateName: editing?.templateName ?? template?.displayName ?? '',
          templateIconKey: editing?.templateIconKey ?? template?.iconKey,
          templateVersion: editing?.templateVersion ?? template?.version.version ?? 1,
          title: snapshot.title, status: 'DRAFT',
          requesterPersonId: editing?.requesterPersonId ?? requester?.personId ?? '',
          requesterName: editing?.requesterName ?? requester?.name,
          values: structuredClone(snapshot.values), fields: structuredClone(composerFields),
          templateApprovalSteps: structuredClone(formApprovalSteps),
          draftApprovalSteps: cloneRequestApprovalSteps(snapshot.approvalSteps),
          createdAt: editing?.createdAt ?? now, updatedAt: now,
        };
        if (action === 'submit') fixtureRequest = submitFixtureWorkflowRequest(fixtureRequest, requesterWorkerId);
      }
      withoutNavigationGuard(() => onComplete(action, result.requestId, fixtureRequest));
    },
    onApprovalStepsNormalized: setApprovalSteps,
  });
  const editingLocked = Boolean(savingAction) || submitRetryPending || checkingSubmission;
  const busy = Boolean(savingAction) || checkingSubmission || pendingFiles.size > 0;
  const leaveBlocked = busy || submitRetryPending || draftSaveStatus === 'saving';
  const checkLatestSubmission = async () => {
    const latest = await checkSubmission();
    if (latest) withoutNavigationGuard(() => onSubmissionResolved(latest));
  };
  const activityVisible = Boolean(activityRequestID && activityOpen);
  const focusField = (fieldKey: string) => {
    const target = document.getElementById(`workflow-field-${composerID}-${fieldKey.replace(/[^a-zA-Z0-9_-]/g, '')}`);
    target?.scrollIntoView({ block: 'nearest' });
    if (target?.matches('input,textarea,button,[contenteditable="true"],[tabindex]')) target.focus();
    else target?.querySelector<HTMLElement>('input,textarea,button,[contenteditable="true"],[tabindex]')?.focus();
  };
  const validationItems = [
    ...(!title.trim() ? [{ id: 'title', label: '요청 제목', message: '제목을 입력해 주세요.', onActivate: () => composerRef.current?.querySelector<HTMLButtonElement>('[aria-label="요청 제목 수정"]')?.click() }] : []),
    ...composerFields.filter((field) => submissionErrors[field.key]).map((field) => ({ id: field.key, label: field.label, message: submissionErrors[field.key], onActivate: () => focusField(field.key) })),
    ...(!approvalStepsAreValid ? [{ id: 'approval', label: '결재선', message: '결재 단계와 담당자를 확인해 주세요.', onActivate: () => setApprovalPreviewOpen(true) }] : []),
  ];
  const submitRequest = () => {
    if (busy || draftSaveConflict) return;
    setValidationAttempt((current) => current + 1);
    if (readyToSubmit) void submit(true);
  };
  const recoveryText = [`요청 제목: ${title}`, ...composerFields.filter((field) => field.type !== 'DISPLAY_TEXT').map((field) => `${field.label}: ${formatWorkflowFieldValue(field, values[field.key], { fields: composerFields, people: personOptions, organizations: directory.data?.organizations })}`), '결재선:', ...approvalSteps.map((step) => `${step.order}. ${step.label}`)].join('\n\n');
  useEffect(() => registerNavigationGuard(() => {
    if (skipUnloadGuardRef.current) return true;
    if (leaveBlocked) return false;
    return !isDirty || window.confirm('저장하지 않은 변경 사항을 버리고 이동할까요?');
  }), [leaveBlocked, isDirty]);
  useEffect(() => {
    if (!isDirty && !leaveBlocked) return undefined;
    const preventAccidentalUnload = (event: BeforeUnloadEvent) => {
      if (skipUnloadGuardRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventAccidentalUnload);
    return () => window.removeEventListener('beforeunload', preventAccidentalUnload);
  }, [isDirty, leaveBlocked]);
  const { confirm, confirmationDialog } = useConfirmation();
  const requestClose = async () => {
    if (leaveBlocked) return;
    if (isDirty && !await confirm({ title: '작성한 변경을 버릴까요?', description: '저장되지 않은 변경은 사라집니다. 마지막으로 저장한 초안은 다시 열 수 있습니다.', confirmLabel: '변경 버리기', intent: 'discard' })) return;
    withoutNavigationGuard(() => onClose(savedDraftID));
  };
  const reopenLatestDraft = async () => {
    if (!await confirm({ title: '최신 초안을 다시 열까요?', description: '이 화면의 저장되지 않은 변경을 버리고 서버에 저장된 최신 초안을 엽니다.', confirmLabel: '최신 초안 열기', intent: 'discard' })) return;
    skipUnloadGuardRef.current = true;
    window.location.reload();
  };
  const openApprovalLine = () => {
    if (!canEditApprovalLine || editingLocked) return;
    setApprovalLineDraft(cloneRequestApprovalSteps(approvalSteps));
    setApprovalLineView('edit');
    setApprovalLineNotice('');
    setApprovalTemplateId('');
    setApprovalLineOpen(true);
  };
  const closeApprovalLine = () => {
    setApprovalLineOpen(false);
    setApprovalLineNotice('');
    setApprovalTemplateId('');
  };
  const loadApprovalTemplate = (templateID: string) => {
    const selectedTemplate = approvalSettings.data?.templates.find((item) => item.id === templateID);
    if (!selectedTemplate) return;
    const lockedSteps = reorderRequestSteps(approvalLineDraft
      .filter((step) => !step.requesterEditable)
      .map((step) => ({ ...step, resolverConfig: { ...(step.resolverConfig ?? {}) } })));
    const lockedStageCount = lockedSteps.reduce((maximum, step) => Math.max(maximum, step.stageOrder), 0);
    const presetSteps = selectedTemplate.approvalSteps.map(({ id: _id, ...step }) => ({
      ...step,
      stageOrder: lockedStageCount + step.stageOrder,
      sourceTemplateStepId: undefined,
      requesterEditable: true,
      resolverConfig: { ...(step.resolverConfig ?? {}) },
    }));
    setApprovalTemplateId(templateID);
    setApprovalLineDraft(reorderRequestSteps([...lockedSteps, ...presetSteps]));
    setApprovalLineNotice(`“${selectedTemplate.name}” 템플릿을 편집 중인 결재선에 불러왔습니다. 변경 적용 전에는 요청에 반영되지 않습니다.`);
  };
  const restoreFormApprovalLine = () => {
    if (!formApprovalSteps) return;
    setApprovalLineDraft(requestApprovalStepsFromTemplate(formApprovalSteps));
    setApprovalTemplateId('');
    setApprovalLineNotice(`“${editing?.templateName ?? template?.displayName ?? '워크플로우'}” 양식의 기본 결재선으로 되돌렸습니다. 변경 적용 전에는 요청에 반영되지 않습니다.`);
  };
  const applyApprovalLine = () => {
    if (!approvalLineDraftIsValid) return;
    setApprovalSteps(cloneRequestApprovalSteps(approvalLineDraft));
    setApprovalLineOpen(false);
    setApprovalLineNotice('');
    setNotice('결재선을 수정했습니다. 이 요청에만 적용됩니다.');
  };
  const draftSavedTime = draftSavedAt?.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' });
  const workspaceSaveStatus: WorkspaceSurfaceStatusDescriptor = draftSaveStatus === 'error' ? {
    state: draftSaveConflict ? 'conflict' : 'error',
    label: draftSaveConflict === 'draft-version' ? '저장 충돌' : draftSaveConflict === 'template-version' ? '양식 변경됨' : '저장 실패',
    detail: draftSaveError || undefined,
    action: {
      label: draftSaveConflict === 'draft-version' ? '최신 열기' : draftSaveConflict === 'template-version' ? '다시 선택' : '다시 시도',
      onSelect: draftSaveConflict === 'draft-version' ? reopenLatestDraft : draftSaveConflict === 'template-version' ? requestClose : retryDraftSave,
    },
  } : draftSaveStatus === 'saving' ? {
    state: 'saving',
  } : draftSaveStatus === 'pending' ? {
    state: 'pending',
  } : draftSaveStatus === 'saved' ? {
    state: 'saved',
    detail: draftSavedTime ? `${draftSavedTime} 임시저장됨` : '임시저장됨',
  } : {
    state: 'idle',
    label: '자동 저장',
  };
  const activeTemplateName = editing?.templateName ?? template?.displayName ?? '워크플로우';
  const activeTemplateVersion = editing?.templateVersion ?? template?.version.version;
  const activeTemplateDescription = template?.version.description?.trim();
  const activeTemplateCategory = template?.category?.trim();
  const activeTemplateIconKey = template ? resolveWorkflowTemplateIconKey(template) : 'DOCUMENT';
  const renderSpecialFieldControl = ({ field, value, onChange, disabled, error, state }: WorkflowCustomFieldRendererProps) => {
    if (field.type === 'PERSON') return <WorkflowPersonPicker label={field.label} state={state} invalid={Boolean(error)} required={field.required} personId={typeof value === 'string' ? value : ''} options={personOptions} disabled={disabled} loading={directory.loading} error={directory.error?.message} onRetry={directory.reload} onChange={onChange} />;
    if (field.type === 'ORGANIZATION') return <WorkflowOrganizationPicker label={field.label} state={state} invalid={Boolean(error)} required={field.required} organizationId={typeof value === 'string' ? value : ''} options={directory.data?.organizations ?? []} disabled={disabled} loading={directory.loading} error={directory.error?.message} onRetry={directory.reload} onChange={onChange} />;
    if (field.type === 'FILE') return <WorkflowFileFieldControl
      scopeKey={composerID}
      field={field}
      value={value}
      disabled={disabled}
      state={state}
      port={port}
      fixtureMode={fixtureMode}
      ensureDraft={ensureDraftForFile}
      onBusyChange={fileBusyChanged}
      onChange={onChange}
    />;
    if (field.type === 'WORKFLOW_REFERENCE') return <WorkflowReferenceFieldControl
      value={value}
      disabled={disabled}
      state={state}
      port={port}
      fixtureMode={fixtureMode}
      fixtureOptions={fixtureReferenceOptions}
      currentRequestID={editing?.id}
      onChange={onChange}
    />;
    return undefined;
  };
  const composer = <Box ref={composerRef} sx={{ height: '100%', minHeight: 0 }}><PageScaffold
    title={<WorkflowRequestTitleInput
      leading={<WorkflowRequestTemplateTitleIcon
        name={activeTemplateName}
        version={activeTemplateVersion}
        iconKey={activeTemplateIconKey}
        category={activeTemplateCategory}
        description={activeTemplateDescription}
      />}
      supporting={<Stack component="span" direction="row" useFlexGap sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}><Typography
        component="span"
        sx={{ color: designTokens.color.pageSupportingText, ...designTokens.typography.ui, fontWeight: 600 }}
      >{activeTemplateName} 양식{activeTemplateCategory ? ` · ${activeTemplateCategory}` : ''}{activeTemplateVersion ? ` · v${activeTemplateVersion}` : ''}</Typography>{smallScreen ? <SaveStatus status={workspaceSaveStatus} /> : null}</Stack>}
      value={title}
      disabled={editingLocked}
      onChange={setTitle}
      error={(template || editing) && !title.trim() ? '제목을 입력하세요.' : undefined}
    />}
    titleLabel="요청 제목"
    width="fluid"
    bodyMode="fill"
    actionsLabel="작성 화면 작업"
    actionsVisibility="always"
    actions={<WorkspaceSurfaceHeaderActions
      compact
      presentation={presentation}
      onPresentationChange={(value) => withoutNavigationGuard(() => onPresentationChange(value))}
      onExit={requestClose}
      presentationDisabled={busy}
      exitDisabled={leaveBlocked}
      status={smallScreen ? undefined : workspaceSaveStatus}
    />}
    editActions={<>
      <Button onClick={requestClose} disabled={leaveBlocked}>취소</Button>
      <Button variant="outlined" onClick={() => void submit(false)} disabled={editingLocked || pendingFiles.size > 0 || Boolean(draftSaveConflict) || !readyToSave}>{savingAction === 'draft' ? '저장 중…' : '임시저장'}</Button>
      <Button variant="contained" onClick={submitRequest} disabled={busy || Boolean(draftSaveConflict)}>{savingAction === 'submit' ? '제출 중…' : submitRetryPending ? '제출 다시 시도' : editing?.status === 'NEEDS_CHANGES' ? '수정 후 재제출' : '요청 제출'}</Button>
    </>}
  >
      <Stack sx={{ height: '100%', minHeight: 0 }} spacing={!desktop && activityVisible ? 1 : 0}>
      {!desktop && activityVisible ? <Stack component="nav" aria-label="작성 문서 바로가기" direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Button size="small" onClick={() => { documentRef.current?.scrollIntoView({ block: 'start' }); documentRef.current?.focus({ preventScroll: true }); }}>문서</Button>
        <Button size="small" onClick={() => {
          if (editing?.status === 'NEEDS_CHANGES') setRevealRevisionKey(value => value + 1);
          else { activityFeedRef.current?.scrollIntoView({ block: 'start' }); activityFeedRef.current?.focus({ preventScroll: true }); }
        }}>활동</Button>
      </Stack> : null}
      <Box data-testid="workflow-request-composer-layout" sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: activityVisible ? 'minmax(0, 1fr) minmax(300px, 360px)' : 'minmax(0, 1fr)' }, gridTemplateRows: { xs: activityVisible ? 'max-content max-content' : 'max-content', md: 'minmax(0, 1fr)' }, gap: { xs: 2.5, md: 3 }, alignItems: { xs: 'start', md: 'stretch' }, overflow: { xs: 'auto', md: 'hidden' } }}>
      <Stack ref={documentRef} tabIndex={-1} aria-label="작성 문서" data-testid="workflow-request-composer-document" spacing={3.5} sx={{ minWidth: 0, minHeight: 0, width: '100%', maxWidth: activityVisible ? 'none' : 960, mx: 'auto', overflowY: { md: 'auto' }, pr: { md: 0.5 } }}>
        {activityRequestID ? <Stack direction="row" useFlexGap sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
          {editing?.status === 'NEEDS_CHANGES' ? <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>재제출하면 기존 결재선의 첫 단계부터 다시 결재합니다.</Typography> : null}
          <Button size="small" startIcon={<HistoryRounded />} aria-expanded={activityVisible} aria-controls={activityPanelID} onClick={() => setActivityOpen((value) => !value)}>{activityVisible ? '활동 이력 닫기' : '활동 이력'}</Button>
        </Stack> : null}
        {validationAttempt > 0 ? <FormErrorSummary items={validationItems} focusRequest={validationAttempt} /> : null}
        {pendingFiles.size > 0 ? <Alert severity="info">첨부 파일 처리가 끝나면 저장하거나 제출할 수 있습니다.</Alert> : null}
        {error && <Alert severity="error" action={submitRetryPending ? <Button color="inherit" size="small" disabled={busy} onClick={() => void checkLatestSubmission()}>{checkingSubmission ? '확인 중…' : '최신 상태 확인'}</Button> : approvalResolutionError && canEditApprovalLine ? <Button color="inherit" size="small" onClick={openApprovalLine}>결재선 수정</Button> : undefined}>
          {error}
          {approvalResolutionError ? <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
            {approvalResolutionError.stepLabel || (approvalResolutionError.stepOrder ? `${approvalResolutionError.stepOrder}단계` : '결재 단계')}의 담당자를 확인해 주세요. {canEditApprovalLine ? '결재선을 수정한 뒤 다시 제출할 수 있습니다.' : '이 결재선은 고정되어 있습니다. 양식 관리자에게 담당자 설정 확인을 요청해 주세요.'}
          </Typography> : null}
        </Alert>}
        {draftSaveConflict ? <Button size="small" onClick={() => setRecoveryOpen(true)} sx={{ alignSelf: 'flex-start' }}>내 입력 내용 보관</Button> : null}
        {draftSaveStatus === 'error' && draftSaveError ? <Alert severity="warning" action={<Button color="inherit" size="small" onClick={draftSaveConflict === 'draft-version' ? reopenLatestDraft : draftSaveConflict === 'template-version' ? requestClose : retryDraftSave}>{draftSaveConflict === 'draft-version' ? '최신 초안 열기' : draftSaveConflict === 'template-version' ? '양식 다시 선택' : '다시 시도'}</Button>}>{draftSaveError}</Alert> : null}
        <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice('')} />
        {(template || editing) && <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Button color="inherit" aria-expanded={approvalPreviewOpen || !approvalStepsAreValid} aria-controls={approvalPreviewID} endIcon={approvalPreviewOpen || !approvalStepsAreValid ? <ExpandLessRounded /> : <ExpandMoreRounded />} onClick={() => setApprovalPreviewOpen((value) => !value)}>예정 결재 {new Set(approvalSteps.map((step) => step.stageOrder)).size}단계</Button>
            {canEditApprovalLine ? <CompactEditButton aria-label="결재선 수정" onClick={openApprovalLine} disabled={editingLocked}>수정</CompactEditButton> : <ValueBadge icon={<LockOutlined />} label="양식에서 고정" />}
          </Stack>
          {approvalPreviewOpen || !approvalStepsAreValid ? <Box id={approvalPreviewID} sx={{ mt: 1.5 }}><WorkflowApprovalLineEditor
          steps={approvalSteps}
          fields={composerFields}
          previews={approvalPreviews}
          approvalLabels={approvalLabels}
          onChange={() => undefined}
          disabled
          showAddStep={false}
          showStepActions={false}
          renderFixedPersonPicker={renderFixedPersonPicker}
          renderFixedRolePicker={renderFixedRolePicker}
          error={approvalSteps.length === 0 ? '승인 단계를 하나 이상 추가하세요.' : undefined}
          getStepError={(step, index) => workflowRequestApprovalStepError(step, index, composerFields)}
          title="결재선"
          hideTitle
        /></Box> : null}
        </Box>}
        {directory.error ? <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={directory.reload}>다시 시도</Button>}
        >승인자 프로필과 조직 정보를 불러오지 못했습니다. 결재선 설정은 유지됩니다.</Alert> : null}
        {approvalRoleOptions.error ? <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={approvalRoleOptions.reload}>다시 시도</Button>}
        >승인 역할을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</Alert> : null}
        {propertyFields.length > 0 || documentFields.length > 0 ? <WorkflowRequestInformationSection
          headingID="workflow-request-information-heading"
          description={activeTemplateDescription}
        >
          <Stack spacing={3}>
            {propertyFields.length > 0 ? <WorkflowRequestFields
              fields={propertyFields}
              controlIdPrefix={composerID}
              disabled={editingLocked}
              values={values}
              errors={fieldErrors}
              ariaLabel="문서 입력 항목"
              renderControl={renderSpecialFieldControl}
              onChange={(fieldKey, value) => { if (!editingLocked) setValues((current) => ({ ...current, [fieldKey]: value })); }}
            /> : null}
            {documentFields.map((field) => <WorkflowRichTextEditor
              key={field.id ?? field.key}
              id={`workflow-field-${composerID}-${field.key.replace(/[^a-zA-Z0-9_-]/g, '')}`}
              disabled={editingLocked}
              label={field.label}
              value={typeof values[field.key] === 'string' ? values[field.key] as string : ''}
              onChange={(value) => { if (!editingLocked) setValues((current) => ({ ...current, [field.key]: value })); }}
              required={field.required}
              error={Boolean(fieldErrors[field.key])}
              helperText={fieldErrors[field.key] ?? field.helpText}
              placeholder="여기에 문서 내용을 작성하세요…"
            />)}
          </Stack>
        </WorkflowRequestInformationSection> : null}
      </Stack>
      {activityVisible ? <Box id={activityPanelID} component="aside" aria-label="요청 활동" sx={{ minWidth: 0, minHeight: 0, height: { md: '100%' }, overflow: { md: 'hidden' } }}>
        <WorkflowActivityTimeline
          appearance="panel"
          feedRef={activityFeedRef}
          revealRevisionKey={revealRevisionKey}
          port={port}
          request={activityRequestID ? { id: activityRequestID, status: editing?.status ?? 'DRAFT' } : undefined}
          fixtureMode={fixtureMode}
          canComment={false}
          refreshKey={activityRefreshKey}
          valueContext={{ fields: composerFields, people: personOptions, organizations: directory.data?.organizations }}
        />
      </Box> : null}
      </Box>
      </Stack>
      <FormDialog
        open={approvalLineOpen}
        onClose={closeApprovalLine}
        onSubmit={applyApprovalLine}
        title="결재선 수정"
        help="양식에서 고정한 단계는 유지하고 수정 가능한 단계만 바꿀 수 있습니다. 저장된 템플릿을 불러오면 수정 가능한 단계가 교체되며 작성 중인 요청은 유지됩니다."
        cancelLabel="변경 취소"
        submitLabel="변경 적용"
        submitDisabled={!approvalLineDraftIsValid}
        maxWidth="md"
      >
        <PillTabs
          value={approvalLineView}
          items={approvalLineDialogTabs}
          ariaLabel="결재선 수정 방식"
          onChange={setApprovalLineView}
        />
        {approvalLineNotice ? <Alert severity="info" onClose={() => setApprovalLineNotice('')}>{approvalLineNotice}</Alert> : null}

        <Box id="request-approval-line-edit-panel" role="tabpanel" aria-labelledby="request-approval-line-edit-tab" hidden={approvalLineView !== 'edit'}>
          <Stack spacing={2}>
            {formApprovalSteps ? <Box>
              <Button size="small" startIcon={<RestartAltOutlined />} onClick={restoreFormApprovalLine}>양식 기본 결재선으로 되돌리기</Button>
            </Box> : null}
            <WorkflowApprovalLineEditor
              steps={approvalLineDraft}
              fields={composerFields}
              previews={approvalLineDraftPreviews}
              approvalLabels={approvalLabels}
              renderFixedPersonPicker={renderFixedPersonPicker}
              renderFixedRolePicker={renderFixedRolePicker}
              onChange={setApprovalLineDraft}
              error={approvalLineDraft.length === 0 ? '승인 단계를 하나 이상 추가하세요.' : undefined}
              getStepError={(step, index) => workflowRequestApprovalStepError(step, index, composerFields)}
              title="이번 요청의 결재선"
            />
          </Stack>
        </Box>

        <Box id="request-approval-line-templates-panel" role="tabpanel" aria-labelledby="request-approval-line-templates-tab" hidden={approvalLineView !== 'templates'}>
          <Stack spacing={1.5}>
            {approvalSettings.loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><LoadingIndicator label="승인 템플릿 불러오는 중" /></Box> : null}
            {approvalSettings.error ? <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={approvalSettings.reload}>다시 시도</Button>}
            >{approvalSettings.error.message}</Alert> : null}
            {!approvalSettings.loading && !approvalSettings.error && (approvalSettings.data?.templates.length ?? 0) === 0 ? <Alert severity="info">등록된 승인 템플릿이 없습니다. 직접 수정하거나 관리자에게 템플릿 등록을 요청하세요.</Alert> : null}
            <Stack spacing={1}>
              {(approvalSettings.data?.templates ?? []).map((item) => <InteractiveCard
                key={item.id}
                onClick={() => loadApprovalTemplate(item.id)}
                aria-label={`${item.name} 승인 템플릿 불러오기`}
                aria-pressed={approvalTemplateId === item.id}
                selected={approvalTemplateId === item.id}
                sx={{ p: 1.5, alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.description || `${item.approvalSteps.length}단계 승인선`}</Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>{item.approvalSteps.map((step) => <ValueBadge key={`${item.id}-${step.order}`} tone={workflowApprovalLabelBadgeTone[step.approvalLabel?.tone ?? 'GREEN']} label={`${step.approvalLabel?.name ?? '승인'} · ${step.label}`} />)}</Stack>
                </Box>
              </InteractiveCard>)}
            </Stack>
          </Stack>
        </Box>
      </FormDialog>
      <FormDialog open={recoveryOpen} onClose={() => setRecoveryOpen(false)} title="내 입력 내용 보관" cancelLabel="닫기" submitLabel="내용 복사" onSubmit={async () => {
        try { await navigator.clipboard.writeText(recoveryText); setNotice('작성 내용을 복사했습니다. 최신 초안을 연 뒤 필요한 내용을 복원할 수 있습니다.'); setRecoveryOpen(false); }
        catch { setNotice('자동 복사가 안 되면 아래 내용을 직접 선택해 복사해 주세요.'); }
      }}>
        <Typography variant="body2">최신 초안을 열기 전에 현재 입력을 복사해 둘 수 있습니다. 첨부 파일은 별도로 보관해 주세요.</Typography>
        <FormTextField label="작성 내용" value={recoveryText} multiline minRows={8} slotProps={{ input: { readOnly: true } }} />
      </FormDialog>
  </PageScaffold></Box>;

  return <><WorkspaceSurface open onClose={requestClose} ariaLabel="워크플로우 요청 작성" presentation={presentation}>
    {composer}
  </WorkspaceSurface>{confirmationDialog}</>;
}
